/* eslint-disable jsx-a11y/media-has-caption */
import { FunctionComponent, useEffect, useState } from 'react'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { getCampaign } from '@api-v2/campaigns'
import { Campaign, DynamicVariables, EnvConfigShare } from '@interfaces'
import { Campaign as SharedCampaign } from '@sendspark/player/dist/types'
import { paramsValidator } from '@constants/paramsValidator'
import Headers from '@components/Headers'
import RenderIf from '@components/RenderIf'
import dynamic from 'next/dynamic'
import useIsOwnerVideo from '@hooks/useIsOwnerVideo'
import {
  useAhoyAnalyticsTracker,
  getAhoyAnalyticsTrackerConfig,
  useAnalytics
} from '@sendspark/analytics'
import { usePlayerTrackingEvents } from '@hooks/lib/usePlayerTrackingEvents'
import { useLocalStorage, useSegmentTracker, useVisitorId } from '@hooks'
import { environmentsConfig } from '@config'
import {
  getDynamicCampaignVariables,
  getDynamicCampaignVariablesTroughParsedQuery,
  getIp
} from '@utils/index'
import { ANALYTICS_TITLE } from '@constants/constants'
import { SegmentEvents } from '@constants/SegementEvents'
import { useRouter } from 'next/router'
import useInterceptXMLHttpRequest from '@hooks/lib/useInterceptXMLHttpRequest'

const Player = dynamic(() => import('@components/Player'), { ssr: false })

type ViewCampaignProps = InferGetServerSidePropsType<typeof getServerSideProps>

const EmbedVideo: FunctionComponent<ViewCampaignProps> = ({
  campaign: initialCampaign,
  isLinkedInRequest,
  dynamicVariables,
  env
}) => {
  const [campaign, setCampaign] = useState(initialCampaign);
  const [loadingCampaign, setLoadingCampaign] = useState(false);

  const [replayVideo, setReplayVideo] = useState(false)

  const { isOwnerVideo } = useIsOwnerVideo({ campaign, env })

  const campaignCreatorWorkspaceId = campaign.creator?.workspaceId

  const segmentTracker = useSegmentTracker(
    env.apiV2Url,
    campaignCreatorWorkspaceId,
    env.cryptoKey
  )
  const visitorId = useVisitorId()

  const { ahoyAnalyticsTracker } = useAhoyAnalyticsTracker({
    config: {
      ...getAhoyAnalyticsTrackerConfig(env.beaconUrl),
      trackVisits: true,
      headers: {
        [process.env.NEXT_PUBLIC_CUSTOM_USER_AGENT || 'X-Snd-Value']:
          process.env.NEXT_PUBLIC_CUSTOM_USER_AGENT_VALUE || 'Sendspark Share'
      }
    },
    debug: env.beaconDebug
  })

  const { analytics } = useAnalytics({
    campaign: (campaign as unknown) as SharedCampaign,

    analyticsTracker: ahoyAnalyticsTracker
  })

  const playerEvents = usePlayerTrackingEvents(
    segmentTracker,
    campaign,
    visitorId
  )

  const [analitycsIdentityValue, _] = useLocalStorage('ahoyData', {})
  const [anonymousId] = useLocalStorage('ajs_anonymous_id', '')
  const router = useRouter()

  // Add interceptor to add visitorToken and visitToken to all analytics requests
  useInterceptXMLHttpRequest((analitycsIdentityValue as any)?.userId || anonymousId || visitorId)

  useEffect(() => {
    if (!campaign?.restricted) return
    
    window.addEventListener('message', async ({ data }) => {
      if (data?.token) {
        setLoadingCampaign(true);
        const completeCampaign = await getCampaign(campaign?.shareId || campaign?.id, undefined, undefined, {
          baseURL: env.apiV2Url,
          headers: {
            Authorization: data.token
          }
        });

        setCampaign(completeCampaign);
        setLoadingCampaign(false);
      }
    })
  }, [])

  useEffect(() => {
    const trackingView = async () => {
      const ip = await getIp()

      // block linkedin prefetch from triggering analytics
      if (isLinkedInRequest || env.excludedIpForAnalytics?.includes(ip)) return

      await segmentTracker(ANALYTICS_TITLE.CampaignView, {
        creatorEmail: campaign.creator.email,
        visitor: visitorId,
        campaignId: campaign.id,
        videoId: campaign.meta.video?.id,
        workspace: {
          id: campaign.creator.workspaceId,
          name: campaign.creator.workspaceName
        },
        creatorId: campaign.creator._id
      })

      await segmentTracker(ANALYTICS_TITLE.ViewGot, {
        viewerId:
          (analitycsIdentityValue as any)?.userId || anonymousId || visitorId,
        shareUrl: window.location.href,
        workspace: {
          id: campaign.creator.workspaceId,
          name: campaign.creator.workspaceName
        },
        creatorId: campaign.creator._id
      })

      await segmentTracker(ANALYTICS_TITLE.VideoViewReceived, {
        viewerId:
          (analitycsIdentityValue as any)?.userId || anonymousId || visitorId,
        shareUrl: window.location.href,
        workspace: {
          id: campaign?.creator?.workspaceId,
          name: campaign?.creator?.workspaceName
        },
        creatorId: campaign?.creator._id
      })

      if (campaign?.featureFlips?.dynamicVariables) {
        const variables: string[] = getDynamicCampaignVariables(
          campaign.meta.heading1 + campaign.meta.heading2
        )
        if (!!variables.length) {
          await segmentTracker(SegmentEvents.dynamicCampaignViewed, {
            dynamicVariables: {
              query: { ...router.query, ...dynamicVariables },
              text: [...variables]
            },
            campaignId: campaign.id,
            creator: campaign.creator.email,
            url: window.location.href,
            workspace: {
              id: campaign.creator.workspaceId,
              name: campaign.creator.workspaceName
            },
            creatorId: campaign.creator._id
          })
        }
      }
      if (
        analytics &&
        !env.excludedIpForBeaconTracking?.includes(ip) &&
        !isOwnerVideo
      ) {
        if (analytics) {
          try {
            analytics?.track('$visit', { video: campaign?.meta?.video })
          } catch (error) {
            console.error('Error tracking visit', error)
          }
        }
      }
    }

    if (analytics) {
      trackingView()
    }
  }, [analytics])
  
  return (
    <>
      <Headers
        title={campaign.meta.documentTitle}
        campaign={campaign}
        // message="Video Message by"
        // background={campaign.style.background}
        sendsparkBrandingEnabled={campaign.sendsparkBrandingEnabled}
        embed
      />
      <RenderIf condition={!loadingCampaign}>
        <Player
          campaign={(campaign as unknown) as SharedCampaign}
          apiV2Url={env.apiV2Url}
          beaconUrl={env.beaconUrl}
                legacyAnalytics={analytics}
          beaconDebug={env.beaconDebug}
          insightUrl={
            env.insightCaptureEnabled && !isOwnerVideo && env.insightUrl
              ? env.insightUrl
              : undefined
          }
          insightApiKey={
            env.insightCaptureEnabled && !isOwnerVideo && env.insightUrl
              ? env.insightApiKey || undefined
              : undefined
          }
          events={isOwnerVideo ? (null as any) : playerEvents}
          apiV1Url={environmentsConfig.apiV1Url}
          nodeEnv={environmentsConfig.nodeEnv}
          autoPlay={campaign?.featureFlips?.autoPlay && campaign?.autoPlay}
          thumbsUp={campaign?.featureFlips?.thumbsUp && campaign?.thumbsUp}
          gapCombinedVideoPlayer={
            campaign?.featureFlips?.gapCombinedVideoPlayer &&
            campaign?.gapCombinedVideoPlayer
          }
          replayVideo={replayVideo}
          setReplayVideo={setReplayVideo}
          embed
        />
      </RenderIf>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<
  {
    campaign: Campaign
    env: EnvConfigShare
    dynamicVariables: DynamicVariables
    isLinkedInRequest: boolean
  },
  { id: string }
> = async (ctx) => {
  const { params, req, query } = ctx;
  const campaignId = params?.id;
  const paramIsValid = paramsValidator(campaignId || '');
  const userAgent = req ? req.headers['user-agent'] : navigator.userAgent;
  const isLinkedInRequest = userAgent?.includes('LinkedIn') ?? false;
  const dynamicVariables = getDynamicCampaignVariablesTroughParsedQuery(query);

  if (!params?.id || !paramIsValid) {
    return {
      notFound: true,
    };
  }

  const campaign = await getCampaign(params.id);

  if (!campaign) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      campaign,
      isLinkedInRequest,
      dynamicVariables,
      env: {
        apiV2Url: process.env.NEXT_PUBLIC_API_V2_URL || '',
        apiV1Url: process.env.NEXT_PUBLIC_API_V1_URL || '',
        pusherKey: process.env.NEXT_PUBLIC_PUSHER_KEY || '',
        pusherCluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER || '',
        nodeEnv: process.env.NEXT_PUBLIC_NODE_ENV || 'development',
        beaconUrl: process.env.NEXT_PUBLIC_BEACON_URL || '',
        insightUrl: process.env.INSIGHT_URL || null,
        insightApiKey: process.env.INSIGHT_API_KEY || null,
        insightCaptureEnabled: process.env.INSIGHT_CAPTURE_ENABLED === 'true',
        beaconDebug: JSON.parse(
          process.env.NEXT_PUBLIC_BEACON_DEBUG || 'false'
        ) as boolean,
        dashboardUrl: process.env.DASHBOARD_URL || '',
        appUrl: process.env.APP_URL || ''
      },
    },
  };
};

export default EmbedVideo;
